"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ApiWaveDecision } from "@/generated/models/ApiWaveDecision";
import { Time } from "@/helpers/time";
import { FULL_APPROVAL_WAVE_DECISIONS_PAGE_SIZE, useWaveDecisions } from "@/hooks/waves/useWaveDecisions";
import { getApprovalWaveCloseStatus, getApprovalWindowEndTime, getApprovedDropsCount, hasApprovalDecisionCounts, isApproveWave, type ApprovalWaveCloseStatus } from "@/helpers/waves/approve-wave.helpers";
interface UseApprovalWaveStatusParams {
  readonly wave: ApiWave | null | undefined;
  readonly areDecisionPointsComplete?: boolean | undefined;
  readonly isDecisionPointsLoadError?: boolean | undefined;
  readonly onRetryDecisionPointsLoad?: (() => void) | undefined;
  /**
   * When defined, decision points are owned by the caller and this hook will
   * not start a second full decision-page load for status checks.
   */
  readonly decisionPoints?: readonly ApiWaveDecision[] | undefined;
}
interface ApprovalWaveStatus {
  readonly winningThreshold: number | null;
  readonly approvedCount: number | null;
  readonly closeStatus: ApprovalWaveCloseStatus;
  readonly isApprovalStatusLoading: boolean;
  readonly isApprovalStatusError: boolean;
  readonly isVotingClosed: boolean;
  readonly isVotingControlsLocked: boolean;
  readonly retryApprovalStatus: (() => void) | null;
}
const getValidThreshold = (threshold: number | null | undefined) => typeof threshold === "number" && Number.isFinite(threshold) && threshold > 0 ? threshold : null;
const isValidApprovalCount = (value: number | null | undefined): value is number => typeof value === "number" && Number.isFinite(value) && value >= 0;
const MAX_TIMEOUT_MS = 2_147_483_647;
export function useApprovalWaveStatus(t0) {
  const $ = _c(48);
  const {
    areDecisionPointsComplete: t1,
    isDecisionPointsLoadError: t2,
    onRetryDecisionPointsLoad,
    wave,
    decisionPoints
  } = t0;
  const areDecisionPointsComplete = t1 === undefined ? false : t1;
  const isDecisionPointsLoadError = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== wave) {
    t3 = isApproveWave(wave);
    $[0] = wave;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const approveWave = t3;
  const [, setStatusClockTick] = useState(0);
  const currentMillis = Time.currentMillis();
  const hasCallerOwnedDecisionPoints = decisionPoints !== undefined;
  let t4;
  if ($[2] !== approveWave || $[3] !== wave) {
    t4 = approveWave && wave ? getValidThreshold(wave.wave.winning_threshold) : null;
    $[2] = approveWave;
    $[3] = wave;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const winningThreshold = t4;
  let t5;
  if ($[5] !== approveWave || $[6] !== areDecisionPointsComplete || $[7] !== decisionPoints || $[8] !== wave) {
    t5 = approveWave && wave ? getApprovedDropsCount({
      areDecisionPointsComplete,
      decisionPoints,
      wave
    }) : 0;
    $[5] = approveWave;
    $[6] = areDecisionPointsComplete;
    $[7] = decisionPoints;
    $[8] = wave;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const providedApprovedCount = t5;
  let t6;
  bb0: {
    if (!approveWave || !wave || areDecisionPointsComplete || hasCallerOwnedDecisionPoints) {
      t6 = false;
      break bb0;
    }
    if (!isValidApprovalCount(wave.wave.max_winners)) {
      t6 = false;
      break bb0;
    }
    if (hasApprovalDecisionCounts(wave)) {
      t6 = false;
      break bb0;
    }
    const endTime = getApprovalWindowEndTime(wave);
    if (endTime !== null && currentMillis >= endTime) {
      t6 = false;
      break bb0;
    }
    t6 = providedApprovedCount === null;
  }
  const shouldLoadCompleteDecisionPoints = t6;
  const t7 = wave?.id ?? "";
  let t8;
  if ($[10] !== shouldLoadCompleteDecisionPoints || $[11] !== t7) {
    t8 = {
      waveId: t7,
      enabled: shouldLoadCompleteDecisionPoints,
      loadAllPages: true,
      pageSize: FULL_APPROVAL_WAVE_DECISIONS_PAGE_SIZE
    };
    $[10] = shouldLoadCompleteDecisionPoints;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const {
    decisionPoints: loadedDecisionPoints,
    hasLoadedAllPages: hasLoadedCompleteDecisionPoints,
    isLoadingAllPagesError: isInternalDecisionPointsLoadError,
    refetch: retryInternalDecisionPointsLoad,
    fetchNextPage: retryInternalNextDecisionPointsPage,
    hasNextPage: hasInternalNextDecisionPointsPage
  } = useWaveDecisions(t8);
  let t9;
  bb1: {
    if (!approveWave || !wave) {
      t9 = 0;
      break bb1;
    }
    if (providedApprovedCount !== null) {
      t9 = providedApprovedCount;
      break bb1;
    }
    let t10;
    if ($[13] !== hasLoadedCompleteDecisionPoints || $[14] !== loadedDecisionPoints || $[15] !== wave) {
      t10 = getApprovedDropsCount({
        areDecisionPointsComplete: hasLoadedCompleteDecisionPoints,
        decisionPoints: loadedDecisionPoints,
        wave
      });
      $[13] = hasLoadedCompleteDecisionPoints;
      $[14] = loadedDecisionPoints;
      $[15] = wave;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    t9 = t10;
  }
  const approvedCount = t9;
  let t10;
  if ($[17] !== approveWave || $[18] !== approvedCount || $[19] !== wave) {
    t10 = approveWave && wave ? getApprovalWaveCloseStatus({
      approvedCount,
      now: currentMillis,
      wave
    }) : null;
    $[17] = approveWave;
    $[18] = approvedCount;
    $[19] = wave;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const closeStatus = t10;
  let t11;
  if ($[21] !== approveWave || $[22] !== approvedCount || $[23] !== closeStatus || $[24] !== wave) {
    t11 = approveWave && wave && isValidApprovalCount(wave.wave.max_winners) && !isValidApprovalCount(wave.wave.no_of_decisions_left) && approvedCount === null && closeStatus === null;
    $[21] = approveWave;
    $[22] = approvedCount;
    $[23] = closeStatus;
    $[24] = wave;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const isApprovalStatusUnknown = Boolean(t11);
  const isApprovalStatusError = Boolean(isApprovalStatusUnknown && (hasCallerOwnedDecisionPoints && isDecisionPointsLoadError || !hasCallerOwnedDecisionPoints && isInternalDecisionPointsLoadError));
  const isApprovalStatusLoading = isApprovalStatusUnknown && !isApprovalStatusError;
  const isVotingClosed = closeStatus !== null;
  const isVotingControlsLocked = isVotingClosed || isApprovalStatusLoading || isApprovalStatusError;
  let t12;
  if ($[26] !== hasInternalNextDecisionPointsPage || $[27] !== retryInternalDecisionPointsLoad || $[28] !== retryInternalNextDecisionPointsPage) {
    t12 = () => {
      if (hasInternalNextDecisionPointsPage) {
        retryInternalNextDecisionPointsPage();
        return;
      }
      retryInternalDecisionPointsLoad();
    };
    $[26] = hasInternalNextDecisionPointsPage;
    $[27] = retryInternalDecisionPointsLoad;
    $[28] = retryInternalNextDecisionPointsPage;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const retryInternalApprovalStatus = t12;
  let t13;
  bb2: {
    if (!isApprovalStatusError) {
      t13 = null;
      break bb2;
    }
    if (hasCallerOwnedDecisionPoints) {
      t13 = onRetryDecisionPointsLoad ?? null;
      break bb2;
    }
    t13 = retryInternalApprovalStatus;
  }
  const retryApprovalStatus = t13;
  let t14;
  if ($[30] !== approveWave || $[31] !== closeStatus || $[32] !== setStatusClockTick || $[33] !== wave) {
    t14 = () => {
      if (!approveWave || !wave || closeStatus !== null) {
        return;
      }
      const endTime_0 = getApprovalWindowEndTime(wave);
      if (endTime_0 === null) {
        return;
      }
      const delay = Math.min(Math.max(endTime_0 - currentMillis, 0), MAX_TIMEOUT_MS);
      const timeoutId = globalThis.setTimeout(() => {
        setStatusClockTick(_temp);
      }, delay);
      return () => {
        globalThis.clearTimeout(timeoutId);
      };
    };
    $[30] = approveWave;
    $[31] = closeStatus;
    $[32] = setStatusClockTick;
    $[33] = wave;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== approveWave || $[36] !== closeStatus || $[37] !== wave) {
    t15 = [approveWave, closeStatus, currentMillis, wave];
    $[35] = approveWave;
    $[36] = closeStatus;
    $[37] = wave;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  useEffect(t14, t15);
  let t16;
  if ($[39] !== approvedCount || $[40] !== closeStatus || $[41] !== isApprovalStatusError || $[42] !== isApprovalStatusLoading || $[43] !== isVotingClosed || $[44] !== isVotingControlsLocked || $[45] !== retryApprovalStatus || $[46] !== winningThreshold) {
    t16 = {
      winningThreshold,
      approvedCount,
      closeStatus,
      isApprovalStatusLoading,
      isApprovalStatusError,
      isVotingClosed,
      isVotingControlsLocked,
      retryApprovalStatus
    };
    $[39] = approvedCount;
    $[40] = closeStatus;
    $[41] = isApprovalStatusError;
    $[42] = isApprovalStatusLoading;
    $[43] = isVotingClosed;
    $[44] = isVotingControlsLocked;
    $[45] = retryApprovalStatus;
    $[46] = winningThreshold;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  return t16;
}
function _temp(tick) {
  return tick + 1;
}